import React from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/Button';

interface StepIndicatorProps {
  current: number;
  total: number;
  labels: string[];
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({ current, total, labels }) => (
  <div className="flex items-center gap-1.5 overflow-x-auto py-1 no-scrollbar">
    {labels.map((label, i) => {
      const idx = i + 1;
      const done = idx < current;
      const active = idx === current;
      return (
        <React.Fragment key={idx}>
          <div className="flex shrink-0 items-center gap-1.5">
            <div
              className={clsx(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
                done
                  ? 'bg-success text-white'
                  : active
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-alt text-text-muted border border-border',
              )}
            >
              {done ? <Check size={11} /> : idx}
            </div>
            <span
              className={clsx(
                'hidden text-[11px] font-medium sm:block whitespace-nowrap',
                active ? 'text-text-primary' : done ? 'text-success' : 'text-text-muted',
              )}
            >
              {label}
            </span>
          </div>
          {idx < total && (
            <div
              className={clsx(
                'h-px flex-1 min-w-[12px] shrink transition-colors',
                done ? 'bg-success/50' : 'bg-border',
              )}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

interface WizardStepShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  current: number;
  total: number;
  stepLabels: string[];
}

export const WizardStepShell: React.FC<WizardStepShellProps> = ({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  nextLabel = 'Next',
  nextDisabled,
  nextLoading,
  current,
  total,
  stepLabels,
}) => (
  <div className="flex flex-col h-full">
    {/* Step header */}
    <div className="mb-5">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-1">
        Step {current} of {total}
      </div>
      <h2 className="font-display text-xl font-bold text-text-primary leading-tight">{title}</h2>
      {subtitle && (
        <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
      )}
    </div>

    {/* Body */}
    <div className="flex-1 overflow-y-auto min-h-0 pr-1 -mr-1">
      {children}
    </div>

    {/* Bottom nav */}
    <div className="mt-6 pt-4 border-t border-border flex items-center justify-between gap-3 shrink-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={!onBack}
        iconLeft={<ChevronLeft size={14} />}
      >
        Back
      </Button>

      <StepIndicator current={current} total={total} labels={stepLabels} />

      <Button
        size="sm"
        onClick={onNext}
        disabled={nextDisabled}
        loading={nextLoading}
        iconRight={<ChevronRight size={14} />}
      >
        {nextLabel}
      </Button>
    </div>
  </div>
);
